/**
 * F 487 — the "Encrypted Block" slash-menu item.
 *
 * WHY THIS IS A SEPARATE FILE
 * ---------------------------
 * The slash menu's `getItems` runs inside `SuggestionMenuController`, which
 * is a CHILD of `<BlockNoteView>`. It REPLACES the default slash menu, so
 * `getItems` must re-emit the built-ins too — returning only our own row
 * would silently delete Heading/Code/Table from the menu. F 392's
 * `inlineDatabaseSuggestion.ts` established the pattern; this file follows it
 * exactly, including the `key` widening (a custom key needs
 * `Omit<…, 'key'> & { key: '…' }`).
 *
 * WHY THE ITEM IS NOT IN THE MENU'S OWN STATE
 * -------------------------------------------
 * The slash menu is a DIFFERENT surface from the drag-handle menu, and it
 * has the same hazard: the item's click handler must reach a seal dialog
 * that outlives the menu. The dialog is owned by `BlockSealController`,
 * which `PageEditor` mounts OUTSIDE `<BlockNoteView>`, and this file calls
 * it through the `BlockSealContext` — the SAME code path the drag-handle
 * "Turn into → Encrypted Block" item uses. One implementation, two surfaces.
 *
 * THE BLOCK IT SEALS
 * ------------------
 * The slash menu fires on an EMPTY block the operator just typed "/" into.
 * The block to seal is that block — `editor.getTextCursorPosition().block`.
 * The engine replaces it in the page's `Block[]` with the sealed stub, so
 * the empty block becomes the locked one rather than leaving a stray empty
 * paragraph behind.
 */

import type { BlockNoteEditor, BlockSchema, InlineContentSchema, StyleSchema } from '@blocknote/core';
import type { DefaultSuggestionItem } from '@blocknote/core/extensions';
import { useBlockSeal } from '../common/BlockSealController';

/** One slash-menu row. The key is widened exactly as F 392's is. */
export type EncryptedBlockSuggestion = Omit<DefaultSuggestionItem, 'key'> & { key: string };

/**
 * The id of the block the text cursor is in, or null when the cursor is
 * not in a block.
 *
 * MEASURED SHAPE: `getTextCursorPosition()` returns a `TextCursorPosition`
 * with a `block` property. A defensive read, because a null cursor would
 * otherwise throw inside the item's click handler — and a throw there
 * happens AFTER the menu has already closed, so the operator would see
 * exactly the "nothing happened" symptom this dispatch is fixing.
 */
function currentBlockId(editor: unknown): string | null {
  try {
    const pos = (editor as {
      getTextCursorPosition?: () => { block?: { id?: string } };
    }).getTextCursorPosition?.();
    const id = pos?.block?.id;
    return typeof id === 'string' && id.length > 0 ? id : null;
  } catch {
    return null;
  }
}

/**
 * Build the "Encrypted Block" slash-menu item.
 *
 * Returns `[]` when the editor is not in a block — the slash menu then
 * shows nothing extra, which is correct: there is no block to seal.
 */
export function getEncryptedBlockSlashMenuItems<
  BSchema extends BlockSchema,
  I extends InlineContentSchema,
  S extends StyleSchema,
>(editor: BlockNoteEditor<BSchema, I, S>): EncryptedBlockSuggestion[] {
  return [
    {
      key: 'encrypted_block',
      title: 'Encrypted Block',
      subtext: 'Seal this block with a passphrase and a public anchor',
      aliases: ['encrypted', 'encrypt', 'seal', 'lock', 'hidden', 'secret'],
      group: 'Advanced',
      onItemClick: (passedEditor?: unknown) => {
        // THE EDITOR MUST COME FROM THE ARGUMENT — the same measured rule
        // F 392 documented for its own items.
        const ed = (passedEditor ?? editor) as BlockNoteEditor<BSchema, I, S>;
        const blockId = currentBlockId(ed);
        if (!blockId) return;

        // The page id is the OTHER half of the key. It comes from the
        // controller's context, not from the editor: the editor knows which
        // document it holds, not which page that document belongs to.
        //
        // The context is read through a module-level hook, so this function
        // cannot call `useBlockSeal()` directly — it is not a component.
        // The bridge is `setSealOpener`, installed by `BlockSealController`
        // when it mounts.
        const seal = sealOpenerRef.current;
        if (!seal) return;
        seal.openSealDialog(seal.pageId, blockId);
      },
    },
  ];
}

/**
 * The bridge between the slash-menu item and the seal controller.
 *
 * WHY A MODULE-LEVEL REF AND NOT A HOOK
 * --------------------------------------
 * `getItems` is a CALLBACK, not a component. It cannot call
 * `useBlockSeal()`. The controller installs itself here when it mounts, and
 * the callback reads the ref at click time — which is late enough that the
 * controller is always mounted by then, and early enough that the value is
 * current.
 *
 * The ref holds the CONTEXT'S page id, because the page changes as the
 * operator navigates and the context is the authority on it.
 */
interface SealOpener {
  pageId: string | null | undefined;
  openSealDialog: (pageId: string | null | undefined, blockId: string) => boolean;
}

const sealOpenerRef: { current: SealOpener | null } = { current: null };

/**
 * Install the seal opener. Called by `BlockSealController` on mount.
 *
 * Exported for the controller and for tests; not part of the public UI API.
 */
export function setSealOpener(opener: SealOpener | null): void {
  sealOpenerRef.current = opener;
}

/** The currently installed opener, for tests. */
export function getSealOpener(): SealOpener | null {
  return sealOpenerRef.current;
}

export default getEncryptedBlockSlashMenuItems;
